import {
	Box,
	ListItemButton,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { Check, CheckCheck, Paperclip } from "lucide-react";
import type { FC } from "react";
import { formatChatListTimestamp } from "web_myplann/Chat/Helpers/chatFormatting";
import { getConversationName } from "web_myplann/Chat/Helpers/List/getConversationName";
import type { ConversationListItemResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { InitialsAvatar } from "web_myplann/Shared/Components/InitialsAvatar";

type Props = {
	conversation: ConversationListItemResponseDTO;
	isLast: boolean;
	onClick: () => void;
};

export const ChatListRow: FC<Props> = ({ conversation, isLast, onClick }) => {
	const { palette, spacingTokens, chat, shape, iconSize } = useTheme();
	const { t } = useResource();

	const { participant, lastMessage, unreadCount, isGroup } = conversation;
	const conversationName = getConversationName(conversation);
	const unread = unreadCount > 0;
	const preview = lastMessage?.isMine
		? `${t(Resources.Chat.youPrefix)} ${lastMessage.content}`
		: (lastMessage?.content ?? "");

	return (
		<ListItemButton
			disableRipple
			divider={!isLast}
			onClick={onClick}
			sx={{
				gap: spacingTokens.md,
				px: spacingTokens.lg,
				py: spacingTokens.md,
				borderColor: "grey.100",
				"&:active": { bgcolor: "grey.50" },
			}}
		>
			<InitialsAvatar
				name={conversationName}
				photo={isGroup ? undefined : (participant.avatar ?? undefined)}
				size={chat.avatar.size.lg}
				initialsVariant="initials"
				flexShrink={0}
			/>

			<Stack flex={1} minWidth={0}>
				<Stack direction="row" alignItems="baseline" gap={spacingTokens.sm}>
					<Typography
						variant={unread ? "bodyStrong" : "bodyMedium"}
						noWrap
						flex={1}
						minWidth={0}
					>
						{conversationName}
					</Typography>
					{lastMessage?.createdAt && (
						<Typography
							variant="hint"
							flexShrink={0}
							color={unread ? "blue.500" : "text.disabled"}
						>
							{formatChatListTimestamp(lastMessage.createdAt)}
						</Typography>
					)}
				</Stack>

				<Stack direction="row" alignItems="center" gap={0.6} mt={0.1}>
					{lastMessage?.isMine &&
						(lastMessage.isRead ? (
							<CheckCheck
								size={13}
								color={palette.blue[400]}
								style={{ flexShrink: 0 }}
							/>
						) : (
							<Check
								size={13}
								color={palette.grey[400]}
								style={{ flexShrink: 0 }}
							/>
						))}
					{lastMessage?.isAttachment && (
						<Paperclip
							size={iconSize.xxs}
							color={palette.grey[400]}
							style={{ flexShrink: 0 }}
						/>
					)}
					<Typography
						variant="caption"
						noWrap
						flex={1}
						minWidth={0}
						fontWeight={unread ? 500 : 400}
						color={unread ? "grey.900" : "grey.500"}
					>
						{preview}
					</Typography>
					{unread && (
						<Box
							width={8}
							height={8}
							borderRadius={shape.pillRadius}
							bgcolor="blue.500"
							flexShrink={0}
						/>
					)}
				</Stack>
			</Stack>
		</ListItemButton>
	);
};
